/**
 * Personalized recommendations page.
 *
 * Loaded via /recommendations/:shopifyCustomerId — fetches cached recommendations
 * from the backend and renders the same UI as the main testing page.
 */

import { AlertCircle, Loader2 } from 'lucide-react';
import type React from 'react';
import { useParams } from 'react-router-dom';
import { usePersonalizedRecommendations } from '../hooks/useRecommendations';
import {
  flattenRecommendationsByProfile,
  isQueuedResponse,
  type RecommendationResult,
} from '../services/api';
import BookGrid, { type BookData } from './BookGrid';

function mapRecommendationsToBookData(result: RecommendationResult): BookData[] {
  return flattenRecommendationsByProfile(result.recommendations_by_profile).map((rec, idx) => {
    const book = rec.book ?? {};
    return {
      title: book.title ?? book.TITLE ?? 'Untitled',
      author: book.author ?? book.AUTHOR,
      publisher: book.publisher ?? book.PUBLISHER,
      price: book.price ?? book.LIST_PRICE ?? 0,
      language: book.language ?? book.LANGUAGE ?? '',
      score: rec.recommendation_score ?? 0,
      explanation: rec.explanation ?? '',
      genres: rec.genres ?? [],
      themes: rec.themes ?? [],
      sources: rec.sources ?? [],
      purchaseProfile: rec.purchase_profile ?? rec.matching_profile,
      isbn: book.book_id ?? book.BOOK_ID ?? book.sku ?? book.SKU ?? '',
      rank: idx + 1,
    };
  });
}

const PersonalizedPage: React.FC = () => {
  const { shopifyCustomerId } = useParams<{ shopifyCustomerId: string }>();
  const { data, isLoading, error } = usePersonalizedRecommendations(shopifyCustomerId ?? '');

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="w-12 h-12 text-indigo-brand-black animate-spin mx-auto" />
          <p className="text-indigo-brand-text/70 text-lg">
            Loading your personalized recommendations&hellip;
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="bg-white border border-indigo-brand-border rounded-sm p-8 max-w-md text-center space-y-4">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto" />
          <h2 className="text-xl text-indigo-brand-text">Something went wrong</h2>
          <p className="text-indigo-brand-text/70">{error.message}</p>
        </div>
      </div>
    );
  }

  if (!data || isQueuedResponse(data)) {
    const message =
      data?.message ?? 'Your recommendations are still being prepared. Please check back shortly.';
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="bg-white border border-indigo-brand-border rounded-sm p-8 max-w-md text-center space-y-4">
          <Loader2 className="w-10 h-10 text-indigo-brand-black animate-spin mx-auto" />
          <h2 className="text-xl text-indigo-brand-text">Almost there!</h2>
          <p className="text-indigo-brand-text/70">{message}</p>
        </div>
      </div>
    );
  }

  const result = data as RecommendationResult;
  const books = mapRecommendationsToBookData(result);

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl text-indigo-brand-text mb-2">
            Your Personalized Recommendations
          </h1>
          <p className="text-indigo-brand-text/50">
            {books.length} book{books.length === 1 ? '' : 's'} picked just for you
          </p>
        </div>

        {/* Book grid */}
        {books.length > 0 ? (
          <BookGrid books={books} />
        ) : (
          <div className="text-center py-16 text-gray-500">No recommendations available yet.</div>
        )}
      </div>
    </div>
  );
};

export default PersonalizedPage;
